/**
 * HTTP service for the phase-gates feature (PX-W6.3, TASK-EDD04F2F): the four
 * onboarding gates, decisions/submits, the engagement phase + crosswalk, and
 * the step-04 version-locked config snapshots. One transport, keyed envelopes
 * reject as `ApiError` (status + backendKey + message).
 */

import { apiClient } from '@/services'
import type {
  ConfigPayloadView,
  ConfigSnapshotRow,
  ConfigSurfaceSnapshot,
  GateDecisionInput,
  PhaseGateView,
  PhaseTransitionInput,
  ProjectPhaseView
} from '../types'

/** List filters accepted by `GET …/gates`. */
export interface GateListFilters {
  /** `true` → only gates approved by override. */
  overridden?: boolean
  /** `'open'` → only gates not yet approved. */
  status?: 'open' | ''
}

const base = (orgId: string, projectId: string): string => `/${orgId}/projects/${projectId}`

export const phaseGateService = {
  /** The four gates with derived status, criteria, ageing (`GET …/gates`). */
  async listGates(
    orgId: string,
    projectId: string,
    filters: GateListFilters = {}
  ): Promise<PhaseGateView[]> {
    const qs = new URLSearchParams()
    if (filters.overridden === true) qs.set('overridden', 'true')
    if (filters.status) qs.set('status', filters.status)
    const query = qs.toString()
    return apiClient.get<PhaseGateView[]>(`${base(orgId, projectId)}/gates${query ? `?${query}` : ''}`)
  },

  /** One gate plus its decision records (`GET …/gates/:gateRef`). */
  async getGate(orgId: string, projectId: string, gateRef: string): Promise<PhaseGateView> {
    return apiClient.get<PhaseGateView>(`${base(orgId, projectId)}/gates/${gateRef}`)
  },

  /**
   * Record the flow.03 submit act (`POST …/gates/:gateRef/submit`).
   * @returns The REFRESHED gate view — the response IS the new state.
   */
  async submitGate(
    orgId: string,
    projectId: string,
    gateRef: string,
    body?: { comments?: string }
  ): Promise<PhaseGateView> {
    return apiClient.post<PhaseGateView>(
      `${base(orgId, projectId)}/gates/${gateRef}/submit`,
      body
    )
  },

  /**
   * Record a decision (`POST …/gates/:gateRef/decision`). A keyed 400 (open
   * prerequisite, already decided, missing comments / override reason /
   * `resolvesCr`) rejects with the server's envelope — the only refusal UI.
   * @returns The refreshed gate view.
   */
  async decide(
    orgId: string,
    projectId: string,
    gateRef: string,
    input: GateDecisionInput
  ): Promise<PhaseGateView> {
    return apiClient.post<PhaseGateView>(
      `${base(orgId, projectId)}/gates/${gateRef}/decision`,
      input
    )
  },

  /** The phase view + crosswalk + ageing (`GET …/phase`). */
  async getPhase(orgId: string, projectId: string): Promise<ProjectPhaseView> {
    return apiClient.get<ProjectPhaseView>(`${base(orgId, projectId)}/phase`)
  },

  /**
   * Move the engagement (`POST …/phase`). Kickoff = the first transition to
   * `discover`; a skip or backwards move needs `override` + reason.
   * @returns The refreshed phase view.
   */
  async transitionPhase(
    orgId: string,
    projectId: string,
    input: PhaseTransitionInput
  ): Promise<ProjectPhaseView> {
    return apiClient.post<ProjectPhaseView>(`${base(orgId, projectId)}/phase`, input)
  },

  /**
   * The agent-data-access review payload (`GET …/gates/:gateRef/payload`) —
   * live (`locked=false`) before approval, the frozen version after.
   */
  async getPayload(
    orgId: string,
    projectId: string,
    gateRef: string,
    version?: number
  ): Promise<ConfigPayloadView> {
    const qs = version === undefined ? '' : `?version=${encodeURIComponent(String(version))}`
    return apiClient.get<ConfigPayloadView>(
      `${base(orgId, projectId)}/gates/${gateRef}/payload${qs}`
    )
  },

  /** Every locked configuration version, newest first (`GET …/config-snapshots`). */
  async listSnapshots(orgId: string, projectId: string): Promise<ConfigSnapshotRow[]> {
    return apiClient.get<ConfigSnapshotRow[]>(`${base(orgId, projectId)}/config-snapshots`)
  },

  /** One immutable locked version, surfaces included (`GET …/config-snapshots/:version`). */
  async getSnapshot(
    orgId: string,
    projectId: string,
    version: number
  ): Promise<ConfigSnapshotRow & { surfaces?: ConfigSurfaceSnapshot[] }> {
    return apiClient.get<ConfigSnapshotRow & { surfaces?: ConfigSurfaceSnapshot[] }>(
      `${base(orgId, projectId)}/config-snapshots/${version}`
    )
  }
}
